import {computeBackground, type BackgroundLayout} from '../lvs-foni'
import {computeArrow} from '../lvs-arrow'
import type {ArrowPlacement} from '../sign-renderer'
import type {SignDef, SignInput} from './types'

export interface SignLayout {
    readonly layout: BackgroundLayout
    readonly arrow?: ArrowPlacement
}

export interface SignLayoutOptions {
    /**
     * If true, ignore the input's `outerWidth` / `outerHeight` overrides
     * and return the pure auto-derived layout. The Size step uses this
     * to display the "minimum from text" alongside the live override.
     */
    readonly ignoreOuter?: boolean
}

/**
 * Compute the full sign layout: background dimensions, row placements,
 * and (if the input requests an arrow) the arrow's absolute placement
 * in sign-frame coordinates.
 *
 * The arrow zone is reserved inside the inner box on the arrow side,
 * via `innerPaddingLeft` / `innerPaddingRight` on `BackgroundInput`.
 * Reservation = `arrow.totalLength + 0.5 h` so the LVS dimensions in
 * figure 705 hold (`0.5h margin │ arrow │ 0.5h gap │ rows │ 0.5h
 * margin`). Rows are pushed toward the non-arrow side and never enter
 * the zone.
 */
export function computeSignLayout(
    _def: SignDef,
    input: SignInput,
    opts: SignLayoutOptions = {}
): SignLayout {
    const outerWidth = opts.ignoreOuter ? undefined : input.outerWidth
    const outerHeight = opts.ignoreOuter ? undefined : input.outerHeight

    if (!input.arrowDirection || input.rows.length === 0) {
        const layout = computeBackground({
            rows: input.rows,
            bgColor: input.bgColor,
            outerWidth,
            outerHeight
        })
        return {layout}
    }

    const direction = input.arrowDirection
    const hMax = Math.max(...input.rows.map(r => r.h))
    const geometry = computeArrow({direction, h: hMax, variant: 'compound'})
    const gap = 0.5 * hMax
    const arrowZone = geometry.totalLength + gap

    const layout = computeBackground({
        rows: input.rows,
        bgColor: input.bgColor,
        outerWidth,
        outerHeight,
        innerPaddingLeft: direction === 'left' ? arrowZone : 0,
        innerPaddingRight: direction === 'right' ? arrowZone : 0
    })

    // Arrow geometry origin = stem-tail center on the central axis.
    // For a left-pointing arrow, vertices extend in -x (back to tail at
    // x=0, tip at x=-totalLength). To land the tip flush with the
    // inner-box left margin (innerBox.x + 0.5h), the origin sits at
    // innerBox.x + 0.5h + totalLength.
    // Mirror for the right-pointing arrow.
    const margin = 0.5 * hMax
    const yMid = layout.innerBox.y + layout.innerBox.height / 2
    const arrowX =
        direction === 'left' ?
            layout.innerBox.x + margin + geometry.totalLength
        :   layout.innerBox.x + layout.innerBox.width - margin - geometry.totalLength

    return {layout, arrow: {geometry, x: arrowX, y: yMid}}
}
